import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ImagePanel } from "@/components/ImagePanel";
import { ResultsPanel } from "@/components/ResultsPanel";
import { InstallPWA } from "@/components/InstallPWA";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main Home Component
const Home = () => {
  const { language, t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleImageSelect = useCallback((file) => {
    setSelectedImage(file);
    setResults(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    toast.success(t.imageLoaded);
  }, [t]);

  const handleAnalyze = async () => {
    if (!selectedImage) {
      toast.error(t.selectImageFirst);
      return;
    }

    setIsLoading(true);
    setResults(null);

    try {
      const reader = new FileReader();
      reader.readAsDataURL(selectedImage);

      reader.onloadend = async () => {
        const base64String = reader.result.split(',')[1];

        try {
          const response = await axios.post(`${API}/analyze`, {
            image_base64: base64String,
            language: language
          });

          setResults(response.data);

          if (response.data.flores.length > 0) {
            toast.success(`${response.data.flores.length} ${t.elementsIdentified}`);
          } else {
            toast.info(t.noFlowersFound);
          }
        } catch {
          toast.error(t.analysisError);
        } finally {
          setIsLoading(false);
        }
      };
    } catch {
      toast.error(t.processingError);
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setResults(null);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F7F2' }} data-testid="home-page">
      <div className="grain-overlay"></div>

      <Header />

      {/* Main Content */}
      <main className="px-6 md:px-12 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl mb-4" style={{ color: '#1A3C34' }}>
              {t.heroTitle}
            </h2>
            <p className="font-body text-sm md:text-base max-w-3xl mx-auto" style={{ color: '#6B705C' }}>
              {t.heroDescription}
            </p>
          </div>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
            {/* Left Column - Upload (2/5) */}
            <div className="lg:col-span-2 space-y-4">
              <ImagePanel
                imagePreview={imagePreview}
                isLoading={isLoading}
                onImageSelect={handleImageSelect}
                onAnalyze={handleAnalyze}
                onReset={handleReset}
              />
            </div>

            {/* Right Column - Results (3/5) */}
            <ResultsPanel results={results} isLoading={isLoading} />
          </div>
        </div>
      </main>

      <Footer />

      {/* PWA Install Banner */}
      <InstallPWA t={t} />
    </div>
  );
};

export default Home;
